/**
 * Currency utility for display-only currency conversion
 * Base currency: INR (all calculations remain in INR)
 */

// Try multiple API endpoints for better reliability
const EXCHANGE_RATE_APIS = [
  'https://api.exchangerate.host/latest?base=INR',
  'https://api.frankfurter.app/latest?from=INR',
  'https://open.er-api.com/v6/latest/INR'
];

const CACHE_KEY = 'currency_exchange_rates';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Fallback exchange rates (approximate, updated periodically)
const FALLBACK_RATES = {
  USD: 0.012, // 1 INR = 0.012 USD (approximately)
  EUR: 0.011, // 1 INR = 0.011 EUR (approximately)
  INR: 1,
};

// Currency symbols and formatting
const CURRENCY_INFO = {
  INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
  USD: { symbol: '$', code: 'USD', name: 'US Dollar' },
  EUR: { symbol: '€', code: 'EUR', name: 'Euro' },
};

/**
 * Fetch exchange rates from API with caching
 */
const fetchExchangeRates = async () => {
  try {
    // Check cache first
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { rates, timestamp } = JSON.parse(cached);
        const now = Date.now();
        
        // Return cached rates if still valid
        if (now - timestamp < CACHE_DURATION && rates && rates.USD && rates.EUR) {
          return rates;
        }
      } catch (e) {
        // Invalid cache, continue to fetch
      }
    }

    // Try each API endpoint
    for (const apiUrl of EXCHANGE_RATE_APIS) {
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });
        
        if (!response.ok) continue;
        
        const data = await response.json();
        
        // Handle different API response formats
        let rates = {};
        if (data.rates) {
          // exchangerate.host format
          rates = data.rates;
        } else if (data.USD || data.EUR) {
          // Direct rates format
          rates = data;
        }
        
        if (rates.USD && rates.EUR) {
          // Ensure INR is always 1
          rates.INR = 1;
          
          // Cache the rates
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              rates,
              timestamp: Date.now(),
            })
          );
          
          return rates;
        }
      } catch (apiError) {
        // Try next API
        continue;
      }
    }
    
    // All APIs failed, throw error
    throw new Error('All exchange rate APIs failed');
  } catch (error) {
    console.warn('Failed to fetch exchange rates, using cached or fallback:', error);
    
    // Try to return cached rates even if expired
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { rates } = JSON.parse(cached);
        if (rates && rates.USD && rates.EUR) {
          return rates;
        }
      }
    } catch (e) {
      // Invalid cache
    }

    // Fallback to default rates
    return FALLBACK_RATES;
  }
};

/**
 * Get exchange rate for a currency
 */
const getExchangeRate = async (currency) => {
  if (currency === 'INR') {
    return 1;
  }

  const rates = await fetchExchangeRates();
  return rates[currency] || 1;
};

/**
 * Format price with currency conversion (display-only)
 * @param {number} inrAmount - Amount in INR (base currency)
 * @param {string} currency - Target currency (INR, USD, EUR)
 * @param {Object} options - Formatting options
 * @returns {Promise<string>} Formatted price string
 */
export const formatPrice = async (inrAmount, currency = 'INR', options = {}) => {
  // Safety checks
  if (inrAmount === null || inrAmount === undefined || isNaN(inrAmount)) {
    return currency === 'INR' ? '₹0' : `${CURRENCY_INFO[currency]?.symbol || '$'}0`;
  }

  const amount = Number(inrAmount);
  if (amount < 0) {
    return currency === 'INR' ? '₹0' : `${CURRENCY_INFO[currency]?.symbol || '$'}0`;
  }

  // If INR, return as-is (no conversion needed)
  if (currency === 'INR') {
    const formatted = options.useCommas !== false 
      ? amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })
      : amount.toFixed(2);
    return `₹${formatted}`;
  }

  // Convert to target currency
  try {
    const rate = await getExchangeRate(currency);
    const convertedAmount = amount * rate;

    // Round to 2 decimal places for display
    const rounded = Math.round(convertedAmount * 100) / 100;

    const formatted = options.useCommas !== false
      ? rounded.toLocaleString('en-US', { 
          minimumFractionDigits: 2,
          maximumFractionDigits: 2 
        })
      : rounded.toFixed(2);

    const symbol = CURRENCY_INFO[currency]?.symbol || '$';
    return `${symbol}${formatted}`;
  } catch (error) {
    console.warn('Currency conversion failed, falling back to INR:', error);
    // Fallback to INR on error
    const formatted = amount.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    return `₹${formatted}`;
  }
};

/**
 * Synchronous version that uses cached rates (for immediate rendering)
 * Falls back to INR if rates not available
 */
export const formatPriceSync = (inrAmount, currency = 'INR', options = {}) => {
  // Safety checks
  if (inrAmount === null || inrAmount === undefined || isNaN(inrAmount)) {
    return currency === 'INR' ? '₹0' : `${CURRENCY_INFO[currency]?.symbol || '$'}0`;
  }

  const amount = Number(inrAmount);
  if (amount < 0) {
    return currency === 'INR' ? '₹0' : `${CURRENCY_INFO[currency]?.symbol || '$'}0`;
  }

  // If INR, return as-is
  if (currency === 'INR') {
    const formatted = options.useCommas !== false 
      ? amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })
      : amount.toFixed(2);
    return `₹${formatted}`;
  }

  // Try to get cached rate
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { rates } = JSON.parse(cached);
      const rate = rates && rates[currency];
      
      if (rate && rate > 0) {
        const convertedAmount = amount * rate;
        const rounded = Math.round(convertedAmount * 100) / 100;
        
        const formatted = options.useCommas !== false
          ? rounded.toLocaleString('en-US', { 
              minimumFractionDigits: 2,
              maximumFractionDigits: 2 
            })
          : rounded.toFixed(2);

        const symbol = CURRENCY_INFO[currency]?.symbol || '$';
        return `${symbol}${formatted}`;
      }
    }
  } catch (error) {
    // Fall through to fallback rates
  }

  // Use fallback rates if no cached rate
  const fallbackRate = FALLBACK_RATES[currency];
  if (fallbackRate) {
    const convertedAmount = amount * fallbackRate;
    const rounded = Math.round(convertedAmount * 100) / 100;
    
    const formatted = options.useCommas !== false
      ? rounded.toLocaleString('en-US', { 
          minimumFractionDigits: 2,
          maximumFractionDigits: 2 
        })
      : rounded.toFixed(2);

    const symbol = CURRENCY_INFO[currency]?.symbol || '$';
    return `${symbol}${formatted}`;
  }

  // Final fallback to INR
  const formatted = amount.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return `₹${formatted}`;
};

/**
 * Get currency info
 */
export const getCurrencyInfo = (currency) => {
  return CURRENCY_INFO[currency] || CURRENCY_INFO.INR;
};

/**
 * Get available currencies
 */
export const getAvailableCurrencies = () => {
  return Object.keys(CURRENCY_INFO);
};

/**
 * Initialize exchange rates (call on app load)
 */
export const initializeExchangeRates = async () => {
  try {
    await fetchExchangeRates();
  } catch (error) {
    console.warn('Failed to initialize exchange rates:', error);
  }
};
